import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { X, ZoomIn, ZoomOut, Maximize2 } from 'lucide-react';
import { cn } from '../../utils/cn';

interface ImageLightboxProps {
  src: string | null | undefined;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  alt?: string;
}

const MIN_ZOOM = 0.5;
const MAX_ZOOM = 3;
const ZOOM_STEP = 0.25;

const ImageLightbox: React.FC<ImageLightboxProps> = ({ src, open, onOpenChange, alt }) => {
  const { t } = useTranslation();
  const [zoom, setZoom] = useState(1);
  const [loaded, setLoaded] = useState(false);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (open) {
      setZoom(1);
      setLoaded(false);
      setFailed(false);
    }
  }, [open, src]);

  /**
   * Escape fallback.
   *
   * Radix's DismissableLayer decides which open layer owns Escape with
   * `index === context.layers.size - 1`, where `index` is captured in a RENDER closure and reaches
   * the document keydown listener through `useCallbackRef` — whose `callbackRef.current = callback`
   * assignment is a PASSIVE effect. A freshly-mounted layer therefore carries `index === -1` until
   * (1) its mount effect adds its node to the module-global layer set, (2) `dispatchUpdate()`
   * forces a re-render, and (3) that render's passive effects flush. Until (3) lands, EVERY layer
   * computes `isHighestLayer === false` and Escape is dropped with no error and no visual symptom.
   *
   * Measured on @radix-ui/react-dialog 1.1.15 / react 18.3.1 in a bundled reproduction of this
   * component: pressing Escape immediately after the lightbox appeared logged
   * `ESC-CB closure RENDER #9 index=-1 size=2 isHighest=false` and both dialogs stayed
   * `data-state="open"`; inserting one extra round trip before the key press produced
   * `index=1 size=2 isHighest=true` and it closed correctly. It is a settle race, not a nesting
   * bug — nesting only widens the window, because the second dialog mounts into a busier tree.
   *
   * This listener makes the close deterministic instead of timing-dependent. It is a pure
   * fallback: `defaultPrevented` is already set by Radix whenever Radix DID handle the key, so
   * this runs only when Radix dropped it. Radix keeps ownership of the portal, focus trap, focus
   * restore and ARIA — only the Escape decision is taken out of the race.
   */
  useEffect(() => {
    if (!open) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape' || event.defaultPrevented) return;
      // Claim the key so no layer underneath this one (the dialog that opened the lightbox) can
      // also act on it and close itself.
      event.preventDefault();
      event.stopPropagation();
      onOpenChange(false);
    };
    document.addEventListener('keydown', onKeyDown, { capture: true });
    return () => document.removeEventListener('keydown', onKeyDown, { capture: true });
  }, [open, onOpenChange]);

  const zoomIn = () => setZoom((z) => Math.min(MAX_ZOOM, +(z + ZOOM_STEP).toFixed(2)));
  const zoomOut = () => setZoom((z) => Math.max(MIN_ZOOM, +(z - ZOOM_STEP).toFixed(2)));
  const reset = () => setZoom(1);

  if (!src) return null;

  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay
          className="fixed inset-0 bg-black/85 z-[60] data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=closed]:animate-out data-[state=closed]:fade-out-0"
        />
        <DialogPrimitive.Content
          className={cn(
            'fixed inset-0 z-[60] flex flex-col items-center justify-center outline-none',
            'data-[state=open]:animate-in data-[state=open]:fade-in-0',
            'data-[state=closed]:animate-out data-[state=closed]:fade-out-0'
          )}
          aria-describedby={undefined}
        >
          <DialogPrimitive.Title className="sr-only">{alt ?? t('Image Preview')}</DialogPrimitive.Title>

          {/* Close */}
          <DialogPrimitive.Close
            aria-label={t('Close Preview') as string}
            className="absolute top-4 ltr:right-4 rtl:left-4 rounded-full bg-white/10 p-2 text-white hover:bg-white/20 focus-visible:ring-2 focus-visible:ring-white/60 outline-none"
          >
            <X className="h-5 w-5" />
          </DialogPrimitive.Close>

          {/* Image area */}
          <div
            className="flex-1 flex items-center justify-center w-full overflow-auto p-6"
            onClick={(e) => { if (e.target === e.currentTarget) onOpenChange(false); }}
          >
            {!failed ? (
              <img
                src={src}
                alt={alt ?? ''}
                onLoad={() => setLoaded(true)}
                onError={() => setFailed(true)}
                className={cn(
                  'max-w-full max-h-full object-contain transition-transform duration-150 select-none',
                  !loaded && 'opacity-0'
                )}
                style={{ transform: `scale(${zoom})`, transformOrigin: 'center center' }}
                draggable={false}
              />
            ) : (
              <div className="text-white/80 text-sm">{t('Unable to load image')}</div>
            )}
            {!loaded && !failed && (
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <div className="h-8 w-8 rounded-full border-2 border-white/30 border-t-white animate-spin" />
              </div>
            )}
          </div>

          {/* Zoom toolbar */}
          <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex items-center gap-1 rounded-full bg-white/10 px-2 py-1 text-white backdrop-blur">
            <button
              type="button"
              onClick={zoomOut}
              aria-label={t('Zoom Out') as string}
              className="rounded-full p-2 hover:bg-white/10 focus-visible:ring-2 focus-visible:ring-white/60 outline-none"
              disabled={zoom <= MIN_ZOOM}
            >
              <ZoomOut className="h-4 w-4" />
            </button>
            <button
              type="button"
              onClick={reset}
              aria-label={t('Reset Zoom') as string}
              className="min-w-[3rem] rounded-full px-2 py-1 text-xs tabular-nums hover:bg-white/10 focus-visible:ring-2 focus-visible:ring-white/60 outline-none"
            >
              {Math.round(zoom * 100)}%
            </button>
            <button
              type="button"
              onClick={zoomIn}
              aria-label={t('Zoom In') as string}
              className="rounded-full p-2 hover:bg-white/10 focus-visible:ring-2 focus-visible:ring-white/60 outline-none"
              disabled={zoom >= MAX_ZOOM}
            >
              <ZoomIn className="h-4 w-4" />
            </button>
            <span className="sr-only"><Maximize2 className="h-4 w-4" /></span>
          </div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
};

export default ImageLightbox;
